/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { formatDate } from '@angular/common';
import { addYears, isPast, parse, parseISO } from 'date-fns';
import { isNil } from 'lodash-es';

export function formatForDatabase(date: string | Date): string {
  if (isNil(date)) {
    return date;
  }
  const dateFormat: string = 'yyyy-MM-dd';
  return formatDate(date, dateFormat, 'de');
}

export function formatFullDateWithTimeWithoutSeconds(date: string | Date): string {
  return formatDate(date, 'dd.MM.yyyy - HH:mm', 'de');
}

export function parseFullDateWithTimeWithoutSeconds(dateStr: string): Date {
  return parse(dateStr, 'dd.MM.yyyy - HH:mm', new Date());
}

export function formatFullDate(date: string | Date): string {
  return formatDate(date, 'dd.MM.yyyy', 'de');
}

export function formatFullDateWithoutSeperator(date: string | Date): string {
  return formatDate(date, 'yyyyMMdd', 'de');
}

export function createFutureDate(todayPlusDays: number): Date {
  const date = new Date();
  date.setDate(date.getDate() + todayPlusDays);

  return date;
}

export function isISODateInPast(isoDate: string): boolean {
  const date: Date = parseISO(isoDate);

  return isPast(date);
}

export function sortByGermanDateStr<T>(entries: T[], getCompareField: (entry: T) => string): T[] {
  return entries.sort((toCompare: T, compareWith: T) => {
    return (
      convertGermanDateString(getCompareField(compareWith)).getTime() -
      convertGermanDateString(getCompareField(toCompare)).getTime()
    );
  });
}

function convertGermanDateString(dateStr: string): Date {
  return new Date(dateStr.replace(/(.*)\.(.*)\.(.*)/, '$3-$2-$1'));
}

// Workaround, solange MatDatepicker genutzt wird.
export function add2000Years(date: Date): Date {
  const year: number = date.getFullYear();
  if (year.toString().length !== 2) {
    return date;
  }

  return addYears(date, 2000);
}
